import { useEffect, useMemo, useState } from 'react';
import api, { errMsg, branchLabel } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeftRight, Check, X, Clock, Loader2 } from 'lucide-react';

export default function OperStok() {
  const { user, branches } = useAuth();
  const isAdmin = user.role === 'admin';
  const [products, setProducts] = useState([]);
  const [transfers, setTransfers] = useState(null);
  const [form, setForm] = useState({
    from: isAdmin ? (branches[0]?.id || 'balaraja') : user.branch,
    to: isAdmin ? (branches[1]?.id || '') : (branches.find((b) => b.id !== user.branch)?.id || ''),
    product_id: '', size: '', qty: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get('/transfers').then((r) => setTransfers(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get('/products').then((r) => setProducts(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const product = products.find((p) => p.id === form.product_id);
  const fromStock = (v) => v?.stock?.[form.from] ?? 0;

  const submit = async () => {
    if (!form.product_id || !form.size) return toast.error('Pilih produk dan ukuran');
    if (!(Number(form.qty) > 0)) return toast.error('Jumlah harus lebih dari 0');
    if (form.from === form.to) return toast.error('Cabang asal dan tujuan harus berbeda');
    setSaving(true);
    try {
      await api.post('/transfers', {
        from_branch: form.from, to_branch: form.to, product_id: form.product_id,
        size: form.size, qty: Number(form.qty), notes: form.notes,
      });
      toast.success('Permintaan oper stok dikirim, menunggu persetujuan admin');
      setForm({ ...form, product_id: '', size: '', qty: '', notes: '' });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const resolve = async (t, action) => {
    try {
      await api.post(`/transfers/${t.id}/${action}`, action === 'reject' ? { reason: 'Ditolak admin' } : {});
      toast.success(action === 'approve' ? 'Transfer disetujui, stok sudah dipindahkan' : 'Transfer ditolak');
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const statusBadge = (s) => {
    if (s === 'pending') return <span className="bg-amber-500/10 text-amber-400 border border-amber-500/25 px-2.5 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1"><Clock className="w-3 h-3" /> Menunggu</span>;
    if (s === 'approved') return <span className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 px-2.5 py-0.5 rounded-full text-xs font-semibold">Disetujui</span>;
    return <span className="bg-brand/10 text-brand border border-brand/25 px-2.5 py-0.5 rounded-full text-xs font-semibold">Ditolak</span>;
  };

  return (
    <div className="space-y-5 animate-fade-up" data-testid="oper-stok-page">
      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Oper Stok</h2>
        <p className="text-sm text-muted-foreground">Transfer stok antar toko — wajib persetujuan admin</p>
      </div>

      <div className="lodi-card p-5" data-testid="transfer-form">
        <h3 className="font-display font-bold mb-4 flex items-center gap-2"><ArrowLeftRight className="w-5 h-5 text-brand" /> Buat Permintaan Transfer</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <span className="lodi-label">Dari Cabang</span>
            {isAdmin ? (
              <select value={form.from} onChange={(e) => { const f = e.target.value; setForm({ ...form, from: f, to: branches.find((b) => b.id !== f)?.id || '' }); }} className="lodi-input" data-testid="transfer-from-select">
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            ) : (
              <p className="text-sm font-semibold py-2">{branchLabel(user.branch)}</p>
            )}
          </div>
          <div>
            <span className="lodi-label">Ke Cabang</span>
            <select value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} className="lodi-input" data-testid="transfer-to-select">
              {branches.filter((b) => b.id !== form.from).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <span className="lodi-label">Produk *</span>
            <select value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value, size: '' })} className="lodi-input" data-testid="transfer-product-select">
              <option value="">Pilih produk</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.article} {p.brand ? `(${p.brand})` : ''}</option>)}
            </select>
          </div>
          <div>
            <span className="lodi-label">Ukuran *</span>
            <select value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} className="lodi-input" disabled={!product} data-testid="transfer-size-select">
              <option value="">Pilih ukuran</option>
              {product?.variants.map((v) => <option key={v.size} value={v.size}>{v.size} — stok {branchLabel(form.from)}: {fromStock(v)}</option>)}
            </select>
          </div>
          <div>
            <span className="lodi-label">Jumlah (pasang) *</span>
            <input type="number" min="1" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="lodi-input" data-testid="transfer-qty-input" />
          </div>
          <div>
            <span className="lodi-label">Catatan</span>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="lodi-input" placeholder="Opsional" data-testid="transfer-notes-input" />
          </div>
        </div>
        <button onClick={submit} disabled={saving} data-testid="transfer-submit-button"
          className="mt-4 px-6 py-3 rounded-xl bg-brand hover:bg-branddark disabled:opacity-40 text-white font-display font-bold flex items-center gap-2 transition-all active:scale-[0.98]">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} KIRIM PERMINTAAN
        </button>
      </div>

      <div className="space-y-3" data-testid="transfer-list">
        <h3 className="font-display font-bold">Daftar Permintaan</h3>
        {!transfers ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-brand animate-spin" /></div>
        ) : transfers.length === 0 ? (
          <p className="text-sm text-muted-foreground">Belum ada permintaan transfer.</p>
        ) : (
          transfers.map((t) => (
            <div key={t.id} className="lodi-card p-4 flex flex-wrap items-center gap-3" data-testid={`transfer-item-${t.id}`}>
              <div className="flex-1 min-w-[200px]">
                <p className="font-semibold text-sm">{t.article} <span className="text-muted-foreground">• Uk {t.size}</span></p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {branchLabel(t.from_branch)} → {branchLabel(t.to_branch)} • {t.qty} psg • oleh {t.requested_by} • {t.date_wib}
                </p>
                {t.notes && <p className="text-xs text-muted-foreground italic">"{t.notes}"</p>}
              </div>
              {statusBadge(t.status)}
              {isAdmin && t.status === 'pending' && (
                <div className="flex gap-2">
                  <button onClick={() => resolve(t, 'approve')} data-testid={`approve-transfer-${t.id}`}
                    className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 flex items-center justify-center hover:bg-emerald-500/20 transition-colors">
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={() => resolve(t, 'reject')} data-testid={`reject-transfer-${t.id}`}
                    className="w-9 h-9 rounded-lg bg-brand/10 border border-brand/30 text-brand flex items-center justify-center hover:bg-brand/20 transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
